import { useState } from "react";
import { TEMPLATES, type TemplateDef } from "./templates";
import { TemplatePreview } from "./TemplatePreview";
import { useI18n } from "../../i18n";

/**
 * Premium template picker (Macro #26 · Increment 3). A curated set of six
 * professional starting points — NOT a marketplace. Applying only populates the
 * owner's Draft (editor working state); Save / Publish stay manual. When the
 * Draft already has content, an explicit confirm guards against silent loss.
 */
export function TemplatePicker({
  existingCount,
  onApply,
  onClose,
}: {
  existingCount: number;
  onApply: (def: TemplateDef) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [pending, setPending] = useState<TemplateDef | null>(null);

  const request = (def: TemplateDef) => {
    if (existingCount > 0) setPending(def);
    else onApply(def);
  };

  return (
    <div
      data-testid="template-picker"
      className="fixed inset-0 z-modal flex items-start justify-center overflow-y-auto bg-black/75 backdrop-blur-sm p-4 md:p-8"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[1080px] my-6 neon-glass-strong rounded-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-4 px-5 md:px-7 h-14 border-b border-border-divider">
          <div className="flex items-center gap-3 min-w-0">
            <span aria-hidden className="h-2 w-2 rotate-45 bg-neon-teal shadow-glow-teal-sm" />
            <span className="font-display text-[11px] font-semibold tracking-[0.24em] uppercase text-neon-teal">
              {t("editor.templates.title")}
            </span>
            <span className="hidden sm:inline font-mono text-[10px] tracking-[0.18em] uppercase text-text-muted">
              {t("editor.templates.curatedStarts")}
            </span>
          </div>
          <button
            data-testid="template-picker-close"
            aria-label={t("editor.templates.close")}
            onClick={onClose}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-text-secondary hover:text-neon-teal hover:bg-elevated"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div className="p-5 md:p-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TEMPLATES.map((def) => (
            <article
              key={def.id}
              data-testid={`template-card-${def.id}`}
              className="group flex flex-col rounded-lg border border-border-soft bg-obsidian/50 overflow-hidden hover:border-border-accent transition-colors duration-med"
            >
              <div className="relative aspect-[4/3] w-full border-b border-border-divider overflow-hidden">
                <TemplatePreview def={def} />
                <span
                  aria-hidden
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-med"
                  style={{ boxShadow: "inset 0 0 40px -8px rgba(45,182,240,0.4)" }}
                />
              </div>
              <div className="flex flex-col flex-1 p-4 gap-2">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-display text-[13px] font-semibold tracking-[0.06em] text-text-primary">
                    {t(`editor.templates.catalog.${def.id}.name`)}
                  </h3>
                  <span className="font-mono text-[9px] tracking-[0.16em] uppercase text-text-muted whitespace-nowrap">
                    {t(`editor.templates.catalog.${def.id}.useCase`)}
                  </span>
                </div>
                <p className="font-display text-[12px] leading-[1.5] text-text-secondary flex-1">
                  {t(`editor.templates.catalog.${def.id}.tagline`)}
                </p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {def.tags.map((_, i) => (
                    <span key={i} className="font-mono text-[9px] tracking-[0.12em] uppercase text-text-muted border border-border-soft rounded-sm px-1.5 py-0.5">
                      {t(`editor.templates.catalog.${def.id}.tag${i}`)}
                    </span>
                  ))}
                </div>
                <button
                  data-testid={`template-apply-${def.id}`}
                  onClick={() => request(def)}
                  className="mt-2 facet-btn h-9 px-4 text-[10px] font-semibold tracking-[0.2em]"
                >
                  {t("editor.templates.apply")}
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {pending && (
        <div
          data-testid="template-confirm"
          className="fixed inset-0 z-toast flex items-center justify-center bg-black/80 p-6"
          onClick={(e) => { e.stopPropagation(); setPending(null); }}
        >
          <div className="w-[min(460px,94vw)] neon-glass-strong rounded-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-[12px] font-semibold tracking-[0.2em] uppercase text-neon-coral mb-2">
              {t("editor.templates.replaceTitle")}
            </div>
            <p className="font-display text-[13px] leading-[1.6] text-text-secondary">
              {t("editor.templates.replaceBody", {
                name: t(`editor.templates.catalog.${pending.id}.name`),
                count: existingCount,
                unit: t(existingCount === 1 ? "editor.templates.elementOne" : "editor.templates.elementOther"),
              })}
            </p>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                data-testid="template-confirm-cancel"
                onClick={() => setPending(null)}
                className="h-9 px-4 rounded-md border border-border-soft text-text-secondary font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-elevated"
              >
                {t("editor.templates.cancel")}
              </button>
              <button
                data-testid="template-confirm-apply"
                onClick={() => { const d = pending; setPending(null); onApply(d); }}
                className="facet-btn facet-btn--coral h-9 px-4 text-[11px] font-semibold tracking-[0.18em]"
              >
                {t("editor.templates.replaceDraft")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
